import { SubscriptionConditionsInline } from '@/components/subscription-conditions'
import { SubscriptionCancellation } from '@/components/subscription-cancellation'
import { SubscriptionPlanChange } from '@/components/subscription-plan-change'
import { TypedCreditList } from '@/components/typed-credit-list'
import { getTotalBalanceCredits } from '@/lib/credit-balances'
import { useTypedCreditBalances } from '@/hooks/use-typed-credit-balances'
import { getCreditWeekStartKey } from '@/lib/date'
import { formatBenefit } from '@/lib/credits-api'
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import {
  CheckIcon,
  CoinsIcon,
  CreditCardIcon,
  GiftIcon,
  Loader2Icon,
  ShoppingBagIcon,
  SparklesIcon,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { PaymentEntriesTable } from '@/components/shared/payment-entries-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  createCustomerPortalSession,
  createSubscriptionCheckoutSession,
  getSubscriptionPackages,
  getUserPaymentEntries,
  getUserPurchasedPackages,
  getUserSubscriptionStatus,
  redeemVoucherCode,
} from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { readBookingReturnPath, rememberBookingReturnPath } from '@/lib/booking-return'
import { queryKeys } from '@/lib/query-keys'
import {
  formatCreditAmount,
  formatCredits,
  isRecurringPackage,
  isRecurringSubscription,
} from '@/lib/subscriptions'
import type { SubscriptionPackage, UserSubscription } from '@/types/domain'

function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`
}

function getEffectivePriceCents(pkg: SubscriptionPackage): number {
  return pkg.effective_price_cents ?? pkg.price_cents
}

function getBasePriceCents(pkg: SubscriptionPackage): number {
  return pkg.base_price_cents ?? pkg.price_cents
}

function hasAppliedMemberDiscount(pkg: SubscriptionPackage): boolean {
  return Boolean(pkg.applied_discount_id && (pkg.applied_discount_amount_cents ?? 0) > 0)
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return format(parsed, 'dd.MM.yyyy', { locale: de })
}

function packageStatusLabel(pkg: {
  entry_kind: 'package' | 'manual_debit' | 'voucher'
  billing_mode: 'payment' | 'subscription'
  remaining_credits: number
  is_active: boolean
}): {
  label: string
  variant: 'default' | 'secondary' | 'destructive'
} {
  if (pkg.entry_kind === 'manual_debit') {
    return { label: 'Abgebucht', variant: 'secondary' }
  }

  if (pkg.entry_kind === 'voucher') {
    return pkg.remaining_credits > 0
      ? { label: 'Gutgeschrieben', variant: 'secondary' }
      : { label: 'Aufgebraucht', variant: 'destructive' }
  }

  if (pkg.billing_mode === 'payment') {
    if (pkg.remaining_credits > 0) {
      return { label: 'Guthaben verfügbar', variant: 'secondary' }
    }

    return { label: 'Aufgebraucht', variant: 'destructive' }
  }

  return pkg.is_active
    ? { label: 'Aktiv', variant: 'default' }
    : { label: 'Inaktiv', variant: 'secondary' }
}

type PaymentStep = 'confirm' | 'redirecting'
type AboDetailsTab = 'purchased-packages' | 'payments'
type PackageOfferTab = 'credit-packages' | 'subscriptions'

function getPackagePriceLabel(pkg: SubscriptionPackage): string {
  if(pkg.type === 'personal_training'){
    return '/ Einheit'
  }
  
  if (isRecurringPackage(pkg)) {
    return '/ Monat'
  }

  return pkg.benefits?.length ? '/ Paket' : `/ ${formatCredits(pkg.credits_included)}`
}

function PackageCard({
  pkg,
  currentSub,
  onSelect,
}: {
  pkg: SubscriptionPackage
  currentSub: UserSubscription | null
  onSelect: (pkg: SubscriptionPackage) => void
}) {
  const isCurrentPlan = currentSub?.package_type === pkg.type && currentSub?.is_active
  const features = [...(pkg.benefits ?? []).map(formatBenefit), ...pkg.features]
  const isPopular = pkg.is_featured

  return (
    <Card
      className={`relative flex h-full flex-col transition-all hover:shadow-lg ${
        isCurrentPlan
          ? 'border-primary border-2 shadow-primary/10 shadow-lg'
          : 'border-border hover:border-primary/40'
      }`}
    >
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-primary-foreground shadow-md">
            {isRecurringPackage(pkg) ? 'Dein aktuelles Abo' : 'Dein aktuelles Paket'}
          </Badge>
        </div>
      )}
      {isPopular && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge variant="secondary" className="shadow-md">
            <SparklesIcon className="mr-1 h-3 w-3" />
            Beliebt
          </Badge>
        </div>
      )}

      <CardHeader className="pb-2 pt-6 text-center">
        <CardTitle className="text-xl">{pkg.name}</CardTitle>
        <div className="mt-2 space-y-1">
          {hasAppliedMemberDiscount(pkg) && (
            <p className="text-sm text-muted-foreground line-through">
              {formatPrice(getBasePriceCents(pkg))}
            </p>
          )}
          <p className="text-3xl font-bold text-primary">{formatPrice(getEffectivePriceCents(pkg))}</p>
        </div>
        <p className="text-xs text-muted-foreground">{getPackagePriceLabel(pkg)}</p>
        {hasAppliedMemberDiscount(pkg) && (
          <p className="text-xs font-medium text-primary">
            Persönlicher Rabatt: {pkg.applied_discount_title} (-{formatPrice(pkg.applied_discount_amount_cents ?? 0)})
          </p>
        )}
        {hasAppliedMemberDiscount(pkg) && (
          <p className="text-xs text-muted-foreground">
            Endpreis nach persönlichem Rabatt
          </p>
        )}
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4">
        <ul className="space-y-2">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Button
          className="mt-auto w-full"
          variant={isCurrentPlan ? 'outline' : 'default'}
          disabled={isCurrentPlan || pkg.can_purchase === false}
          onClick={() => onSelect(pkg)}
        >
          {isCurrentPlan
            ? isRecurringPackage(pkg) ? 'Aktuelles Abo' : 'Aktuelles Paket'
            : pkg.can_purchase === false
              ? 'Freischaltung erforderlich'
              : isRecurringPackage(pkg) ? 'Abo wählen' : 'Paket wählen'}
        </Button>
      </CardContent>
    </Card>
  )
}

export function MemberAboPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [bookingReturnTo] = useState(() => readBookingReturnPath(searchParams))
  const queryClient = useQueryClient()
  const handledCheckoutResultRef = useRef<string | null>(null)
  const [selectedPackage, setSelectedPackage] = useState<SubscriptionPackage | null>(null)
  const [paymentStep, setPaymentStep] = useState<PaymentStep>('confirm')
  const [purchasedPackagesPage, setPurchasedPackagesPage] = useState(1)
  const [purchasedPackagesPageSize, setPurchasedPackagesPageSize] = useState(
    DEFAULT_TABLE_PAGE_SIZE,
  )
  const [billingEntriesPage, setBillingEntriesPage] = useState(1)
  const [billingEntriesPageSize, setBillingEntriesPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
  const [aboDetailsTab, setAboDetailsTab] = useState<AboDetailsTab>('purchased-packages')
  const [voucherCode, setVoucherCode] = useState('')
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false)
  const [packagesDialogOpen, setPackagesDialogOpen] = useState(searchParams.get('angebote') === '1')
  const [packageOfferTab, setPackageOfferTab] = useState<PackageOfferTab>('credit-packages')

  const checkoutResult = searchParams.get('checkout')

  useEffect(() => {
    rememberBookingReturnPath(bookingReturnTo)
  }, [bookingReturnTo])

  useEffect(() => {
    const resetRedirect = () => setPaymentStep('confirm')
    window.addEventListener('pageshow', resetRedirect)
    return () => window.removeEventListener('pageshow', resetRedirect)
  }, [])

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.userSubscription,
    queryFn: getUserSubscriptionStatus,
    retry: false,
  })

  const creditBalancesQuery = useTypedCreditBalances({ weekStarts: [getCreditWeekStartKey(new Date())] })

  const packagesQuery = useQuery({
    queryKey: queryKeys.subscriptionPackages,
    queryFn: getSubscriptionPackages,
  })

  const purchasedPackagesQuery = useQuery({
    queryKey: queryKeys.userPurchasedPackages,
    queryFn: getUserPurchasedPackages,
  })

  const paymentEntriesQuery = useQuery({
    queryKey: queryKeys.userPaymentEntries,
    queryFn: getUserPaymentEntries,
  })

  useEffect(() => {
    if (!checkoutResult) {
      handledCheckoutResultRef.current = null
      return
    }

    if (handledCheckoutResultRef.current === checkoutResult) {
      return
    }

    handledCheckoutResultRef.current = checkoutResult

    if (checkoutResult === 'success') {
      toast.success('Zahlung abgeschlossen. Das Paket wird jetzt aktiviert.')
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.userCreditBalancesBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.userPurchasedPackages })
      queryClient.invalidateQueries({ queryKey: queryKeys.userPaymentEntries })
    } else if (checkoutResult === 'cancelled') {
      toast.info('Checkout wurde abgebrochen.')
    }

    navigate('/abo', { replace: true })
  }, [checkoutResult, navigate, queryClient])

  const checkoutMutation = useMutation({
    mutationFn: (packageType: string) => createSubscriptionCheckoutSession(packageType),
    retry: false,
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: (error) => {
      setPaymentStep('confirm')
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setPaymentStep('confirm'),
  })

  const portalMutation = useMutation({
    mutationFn: createCustomerPortalSession,
    retry: false,
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: (error) => {
      setPaymentStep('confirm')
      toast.error(getErrorMessage(error))
    },
    onSettled: () => setPaymentStep('confirm'),
  })

  const voucherMutation = useMutation({
    mutationFn: (code: string) => redeemVoucherCode(code),
    retry: false,
    onSuccess: (result) => {
      setVoucherCode('')
      setVoucherDialogOpen(false)
      toast.success(
        `${formatCredits(result.credits_granted)}${result.credit_name ? ` (${result.credit_name})` : ''} ${result.credits_granted === 1 ? 'wurde' : 'wurden'} deinem Guthaben gutgeschrieben.`,
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
    onSettled: () => {
      // The server may have committed even when its response was lost.
      queryClient.invalidateQueries({ queryKey: queryKeys.userCreditBalancesBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.userPurchasedPackages })
    },
  })

  const currentSub = subscriptionQuery.data ?? null
  const packages = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data])
  const creditPackages = useMemo(
    () => packages.filter((pkg) => !isRecurringPackage(pkg)),
    [packages],
  )
  const recurringPackages = useMemo(
    () => packages.filter((pkg) => isRecurringPackage(pkg)),
    [packages],
  )
  const purchasedPackages = useMemo(
    () => purchasedPackagesQuery.data ?? [],
    [purchasedPackagesQuery.data],
  )
  const paymentEntries = useMemo(() => paymentEntriesQuery.data ?? [], [paymentEntriesQuery.data])
  const totalPurchasedPackages = purchasedPackages.length
  const totalPaymentEntries = paymentEntries.length
  const purchasedPackagesTotalPages = Math.max(
    1,
    Math.ceil(totalPurchasedPackages / purchasedPackagesPageSize),
  )
  const billingEntriesTotalPages = Math.max(
    1,
    Math.ceil(totalPaymentEntries / billingEntriesPageSize),
  )
  const purchasedPackagesCurrentPage = Math.min(
    purchasedPackagesPage,
    purchasedPackagesTotalPages,
  )
  const billingEntriesCurrentPage = Math.min(billingEntriesPage, billingEntriesTotalPages)
  const paginatedPurchasedPackages = useMemo(() => {
    const from = (purchasedPackagesCurrentPage - 1) * purchasedPackagesPageSize
    return purchasedPackages.slice(from, from + purchasedPackagesPageSize)
  }, [purchasedPackages, purchasedPackagesCurrentPage, purchasedPackagesPageSize])
  const paginatedBillingEntries = useMemo(() => {
    const from = (billingEntriesCurrentPage - 1) * billingEntriesPageSize
    return paymentEntries.slice(from, from + billingEntriesPageSize)
  }, [paymentEntries, billingEntriesCurrentPage, billingEntriesPageSize])

  const currentPlanIsRecurring = isRecurringSubscription(currentSub)
  const cancellationPlanned = Boolean(
    currentSub && currentPlanIsRecurring && (currentSub.stripe_cancel_at_period_end || currentSub.stripe_cancel_at),
  )
  // A failed renewal payment that Stripe still retries: cancelling and fixing the payment method stay possible.
  const paymentRetry = Boolean(currentPlanIsRecurring && currentSub?.stripe_status === 'past_due')

  function isMemberPlanChange(pkg: SubscriptionPackage): boolean {
    if (!currentSub?.is_active) {
      return false
    }

    if (!currentPlanIsRecurring || !isRecurringPackage(pkg)) {
      return false
    }

    return currentSub.package_type !== pkg.type
  }

  const selectedIsMemberPlanChange = selectedPackage
    ? isMemberPlanChange(selectedPackage)
    : false

  function handleOpenPackagesDialog() {
    const hasActiveSubscription = Boolean(currentSub?.is_active && currentPlanIsRecurring)

    setPackageOfferTab(
      hasActiveSubscription && recurringPackages.length > 0
        ? 'subscriptions'
        : creditPackages.length > 0
          ? 'credit-packages'
          : 'subscriptions',
    )
    setPackagesDialogOpen(true)
  }

  function handleSelectPackage(pkg: SubscriptionPackage) {
    setPackagesDialogOpen(false)
    setSelectedPackage(pkg)
    setPaymentStep('confirm')
  }

  function handleCloseDialog() {
    if (paymentStep === 'redirecting') return
    setSelectedPackage(null)
    setPaymentStep('confirm')
  }

  function handleOpenCustomerPortal() {
    if (portalMutation.isPending) return
    setPaymentStep('redirecting')
    portalMutation.mutate()
  }

  function handleConfirmPurchase() {
    if (!selectedPackage || checkoutMutation.isPending || portalMutation.isPending) return

    setPaymentStep('redirecting')

    if (isMemberPlanChange(selectedPackage)) {
      portalMutation.mutate()
      return
    }

    checkoutMutation.mutate(selectedPackage.type)
  }

  function handleRedeemVoucher(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (voucherMutation.isPending) return
    const normalizedCode = voucherCode.trim()
    if (!normalizedCode) {
      toast.error(getErrorMessage(new Error('VOUCHER_CODE_REQUIRED')))
      return
    }

    voucherMutation.mutate(normalizedCode)
  }

  const redirectPending = paymentStep === 'redirecting'
  const availableCredits = getTotalBalanceCredits(creditBalancesQuery.data)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Mein Abo</h1>
        <p className="text-sm text-muted-foreground">Verwalte dein Kurspaket und buche Guthaben hinzu.</p>
      </div>
      {bookingReturnTo && (
        <Button asChild variant="outline">
          <Link to={bookingReturnTo}>Zur Kursbuchung zurück</Link>
        </Button>
      )}

      {subscriptionQuery.isLoading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="py-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {currentPlanIsRecurring && currentSub ? (
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-background">
              <CardContent className="flex h-full min-h-48 flex-col gap-4 py-5">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <CreditCardIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Abo
                    </p>
                    <p className="text-lg font-semibold">{currentSub.package_name}</p>
                  </div>
                  <Badge
                    variant={
                      cancellationPlanned
                        ? 'secondary'
                        : currentSub.is_active
                          ? 'default'
                          : 'destructive'
                    }
                  >
                    {cancellationPlanned
                      ? 'Kündigung geplant'
                      : currentSub.is_active
                        ? 'Aktiv'
                        : paymentRetry
                          ? 'Zahlung offen'
                          : 'Inaktiv'}
                  </Badge>
                </div>

                <SubscriptionConditionsInline packageName={currentSub.package_name} />

                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Dein Wochenkontingent wird für jede Kalenderwoche neu bereitgestellt.
                  </p>
                  {paymentRetry && (
                    <p className="text-xs text-destructive">
                      Die letzte Abo-Zahlung ist fehlgeschlagen. Bitte aktualisiere deine Zahlungsdaten;
                      bis dahin kannst du keine neuen Termine über das Abo buchen.
                    </p>
                  )}
                  {cancellationPlanned && (currentSub.stripe_cancel_at || currentSub.current_period_end) && (
                    <p className="text-xs text-amber-600">
                      Kündigung zum{' '}
                      {format(new Date((currentSub.stripe_cancel_at || currentSub.current_period_end)!), 'dd.MM.yyyy', {
                        locale: de,
                      })}
                      . Bis dahin bleibt dein Abo aktiv.
                    </p>
                  )}
                  {currentSub.expires_at && (
                    <p className="text-xs text-muted-foreground">
                      Gültig bis:{' '}
                      {format(new Date(currentSub.expires_at), 'dd.MM.yyyy', { locale: de })}
                    </p>
                  )}
                </div>

                {(currentSub.is_active || paymentRetry) && (
                  <div className="mt-auto flex flex-wrap justify-end gap-2">
                    <p className="w-full text-sm text-muted-foreground">
                      Rechnungen und Zahlungsdaten werden bei Stripe verwaltet. Der Button öffnet das externe Stripe-Kundenportal.
                    </p>
                    <SubscriptionCancellation planned={cancellationPlanned} />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={redirectPending || portalMutation.isPending}
                      onClick={handleOpenCustomerPortal}
                    >
                      {portalMutation.isPending ? (
                        <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCardIcon className="mr-2 h-4 w-4" />
                      )}
                      Rechnungen und Zahlungsdaten
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-muted-foreground/30 bg-muted/20">
              <CardContent className="flex h-full min-h-48 flex-col gap-4 py-5">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted">
                    <CreditCardIcon className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Abo
                    </p>
                    <p className="text-lg font-semibold">Kein aktives Abo</p>
                  </div>
                  <Badge variant="secondary">Nicht aktiv</Badge>
                </div>

                <p className="text-sm text-muted-foreground">
                  Du besitzt aktuell kein aktives Wochenabo. Unter „Weitere Aktionen“ kannst du ein
                  passendes Paket oder Abo wählen.
                </p>
              </CardContent>
            </Card>
          )}

          <Card className="border-amber-500/25 bg-gradient-to-br from-amber-500/5 to-background">
            <CardContent className="flex h-full min-h-48 flex-col gap-4 py-5">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                  <CoinsIcon className="h-6 w-6 text-amber-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Guthaben
                  </p>
                  {creditBalancesQuery.isLoading ? (
                    <Skeleton className="mt-1 h-10 w-full" />
                  ) : (
                    <TypedCreditList balances={creditBalancesQuery.data} className="mt-1" />
                  )}
                </div>
                <Badge variant="secondary">
                  {availableCredits > 0 ? 'Verfügbar' : 'Kein Guthaben'}
                </Badge>
              </div>

              <p className="text-sm text-muted-foreground">
                {currentPlanIsRecurring
                  ? 'Dieses Guthaben wird ergänzend verwendet, wenn dein Wochenkontingent nicht ausreicht.'
                  : 'Dieses Guthaben kannst du flexibel für Kursbuchungen verwenden.'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="flex min-h-0 flex-col overflow-hidden">
        <Tabs
          value={aboDetailsTab}
          onValueChange={(value) => setAboDetailsTab(value as AboDetailsTab)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <CardHeader className="flex shrink-0 flex-col gap-3">
            <CardTitle className="text-lg">Pakete und Zahlungen</CardTitle>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <TabsList className="h-auto w-full justify-start gap-1 p-1 md:w-auto">
                <TabsTrigger value="purchased-packages">Gekaufte Pakete</TabsTrigger>
                <TabsTrigger value="payments">Zahlungen</TabsTrigger>
              </TabsList>

              {aboDetailsTab === 'purchased-packages' ? (
                <Select
                  value={String(purchasedPackagesPageSize)}
                  onValueChange={(value) => {
                    setPurchasedPackagesPageSize(Number(value))
                    setPurchasedPackagesPage(1)
                  }}
                >
                  <SelectTrigger className="w-full bg-card md:w-[9rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} pro Seite
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select
                  value={String(billingEntriesPageSize)}
                  onValueChange={(value) => {
                    setBillingEntriesPageSize(Number(value))
                    setBillingEntriesPage(1)
                  }}
                >
                  <SelectTrigger className="w-full bg-card md:w-[9rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size} pro Seite
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>

          <CardContent className="min-h-0 flex-1 overflow-hidden">
            <TabsContent value="purchased-packages" className="mt-0 flex min-h-0 flex-col">
              {purchasedPackagesQuery.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : totalPurchasedPackages === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Pakete vorhanden.</p>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-4">
                  <Table containerClassName="min-h-0 flex-1 max-h-none">
                      <TableHeader>
                      <TableRow>
                        <TableHead>Paket</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Info</TableHead>
                        <TableHead>Gekauft am</TableHead>
                        <TableHead>Gültig bis</TableHead>
                        <TableHead>Gekaufte Credits</TableHead>
                        <TableHead>Abbuchungen</TableHead>
                        <TableHead>Verbleibende Credits</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedPurchasedPackages.map((pkg) => {
                        const status = packageStatusLabel(pkg)

                        return (
                          <TableRow key={`${pkg.id}:${pkg.credit_type_id ?? "subscription"}`}>
                            <TableCell>
                              {pkg.entry_kind === 'voucher'
                                ? 'Gutschein'
                                : pkg.package_type === 'manual_credit'
                                ? 'Manuelle Buchung'
                                : pkg.package_name}{pkg.credit_name && <p className="text-xs text-muted-foreground">{pkg.credit_name}</p>}
                            </TableCell>
                            <TableCell>
                              {pkg.entry_kind === 'voucher'
                                ? 'Gutschein'
                                : pkg.billing_mode === 'subscription'
                                  ? 'Abo'
                                  : 'Guthaben'}
                            </TableCell>
                            <TableCell>
                              {pkg.entry_kind === 'voucher'
                                ? (pkg.manual_reason ?? '-')
                                : pkg.package_type === 'manual_credit'
                                  ? (pkg.manual_reason ?? '-')
                                  : '-'}
                            </TableCell>
                            <TableCell>{formatDateTime(pkg.created_at)}</TableCell>
                            <TableCell>{formatDateOnly(pkg.expires_at)}</TableCell>
                            <TableCell>{pkg.purchased_credits == null ? '-' : formatCreditAmount(pkg.purchased_credits)}</TableCell>
                            <TableCell>{pkg.debited_credits > 0 ? formatCreditAmount(pkg.debited_credits) : '-'}</TableCell>
                            <TableCell>
                              {pkg.entry_kind === 'manual_debit' ||
                              pkg.billing_mode === 'subscription'
                                ? '-'
                                : formatCreditAmount(pkg.remaining_credits)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={status.variant}>{status.label}</Badge>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <p>
                      {(purchasedPackagesCurrentPage - 1) * purchasedPackagesPageSize + 1} -{' '}
                      {Math.min(
                        (purchasedPackagesCurrentPage - 1) * purchasedPackagesPageSize +
                          paginatedPurchasedPackages.length,
                        totalPurchasedPackages,
                      )}{' '}
                      von {totalPurchasedPackages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPurchasedPackagesPage(Math.max(1, purchasedPackagesCurrentPage - 1))
                        }
                        disabled={purchasedPackagesCurrentPage <= 1}
                      >
                        Zurück
                      </Button>
                      <span>
                        Seite {purchasedPackagesCurrentPage} / {purchasedPackagesTotalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setPurchasedPackagesPage(
                            Math.min(purchasedPackagesTotalPages, purchasedPackagesCurrentPage + 1),
                          )
                        }
                        disabled={purchasedPackagesCurrentPage >= purchasedPackagesTotalPages}
                      >
                        Weiter
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="payments" className="mt-0 flex min-h-0 flex-col">
              {paymentEntriesQuery.isLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : totalPaymentEntries === 0 ? (
                <p className="text-sm text-muted-foreground">Keine Zahlungen vorhanden.</p>
              ) : (
                <div className="flex min-h-0 flex-1 flex-col gap-4">
                  <PaymentEntriesTable entries={paginatedBillingEntries} />
                  <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                    <p>
                      {(billingEntriesCurrentPage - 1) * billingEntriesPageSize + 1} -{' '}
                      {Math.min(
                        (billingEntriesCurrentPage - 1) * billingEntriesPageSize +
                          paginatedBillingEntries.length,
                        totalPaymentEntries,
                      )}{' '}
                      von {totalPaymentEntries}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBillingEntriesPage(Math.max(1, billingEntriesCurrentPage - 1))
                        }
                        disabled={billingEntriesCurrentPage <= 1}
                      >
                        Zurück
                      </Button>
                      <span>
                        Seite {billingEntriesCurrentPage} / {billingEntriesTotalPages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setBillingEntriesPage(
                            Math.min(billingEntriesTotalPages, billingEntriesCurrentPage + 1),
                          )
                        }
                        disabled={billingEntriesCurrentPage >= billingEntriesTotalPages}
                      >
                        Weiter
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">Weitere Aktionen</p>
            <p className="text-sm text-muted-foreground">
              Löse einen Gutschein ein oder wähle ein neues Paket.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => setVoucherDialogOpen(true)}>
              <GiftIcon className="mr-2 h-4 w-4" />
              Gutschein einlösen
            </Button>
            <Button onClick={handleOpenPackagesDialog}>
              <ShoppingBagIcon className="mr-2 h-4 w-4" />
              Pakete und Abos
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={voucherDialogOpen} onOpenChange={setVoucherDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GiftIcon className="h-5 w-5 text-primary" />
              Gutschein einlösen
            </DialogTitle>
            <DialogDescription>
              Gib deinen Gutschein-Code ein. Das Guthaben wird sofort deinem Konto gutgeschrieben.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleRedeemVoucher}>
            <Input
              value={voucherCode}
              onChange={(event) => setVoucherCode(event.target.value)}
              placeholder="Gutschein-Code"
              autoComplete="off"
              className="font-mono uppercase"
              disabled={voucherMutation.isPending}
              autoFocus
            />
            <DialogFooter>
              <Button
                type="submit"
                disabled={voucherMutation.isPending || voucherCode.trim().length === 0}
              >
                {voucherMutation.isPending ? (
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GiftIcon className="mr-2 h-4 w-4" />
                )}
                Einlösen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={packagesDialogOpen} onOpenChange={setPackagesDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Pakete und Abos</DialogTitle>
            <DialogDescription>
              Wähle flexibles Kursguthaben oder ein monatliches Abo.
            </DialogDescription>
          </DialogHeader>

          {packagesQuery.isLoading ? (
            <div className="grid gap-4 py-2 md:grid-cols-2">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          ) : packagesQuery.isError ? (
            <div className="space-y-3 py-4" role="alert">
              <p className="text-sm text-destructive">{getErrorMessage(packagesQuery.error)}</p>
              <Button variant="outline" onClick={() => packagesQuery.refetch()} disabled={packagesQuery.isFetching}>Erneut laden</Button>
            </div>
          ) : creditPackages.length === 0 && recurringPackages.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Aktuell sind keine Pakete oder Abos verfügbar.
            </p>
          ) : (
            <Tabs
              value={packageOfferTab}
              onValueChange={(value) => setPackageOfferTab(value as PackageOfferTab)}
            >
              <TabsList className="grid h-auto w-full grid-cols-2 p-1">
                <TabsTrigger
                  value="credit-packages"
                  disabled={creditPackages.length === 0}
                  className="gap-2"
                >
                  <CoinsIcon className="h-4 w-4" />
                  Pakete
                  <span className="rounded-full bg-background/80 px-2 py-0.5 text-xs">
                    {creditPackages.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger
                  value="subscriptions"
                  disabled={recurringPackages.length === 0}
                  className="gap-2"
                >
                  <CreditCardIcon className="h-4 w-4" />
                  Abos
                  <span className="rounded-full bg-background/80 px-2 py-0.5 text-xs">
                    {recurringPackages.length}
                  </span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="credit-packages" className="mt-4">
                <section className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
                      <CoinsIcon className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Pakete</h3>
                      <p className="text-xs text-muted-foreground">
                        Einmal zahlen, Guthaben flexibel nutzen.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4">
                    {creditPackages.map((pkg) => (
                      <div key={pkg.id} className="w-full md:w-[calc(50%-0.5rem)]">
                        <PackageCard
                          pkg={pkg}
                          currentSub={currentSub}
                          onSelect={handleSelectPackage}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              </TabsContent>

              <TabsContent value="subscriptions" className="mt-4">
                <section className="rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
                  <div className="mb-4 flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <CreditCardIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Abos</h3>
                      <p className="text-xs text-muted-foreground">
                        Wöchentliches Creditkontingent für regelmäßiges Training.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4">
                    {recurringPackages.map((pkg) => (
                      <div key={pkg.id} className="w-full md:w-[calc(50%-0.5rem)]">
                        <PackageCard
                          pkg={pkg}
                          currentSub={currentSub}
                          onSelect={handleSelectPackage}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={selectedPackage !== null} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="sm:max-w-md">
          {paymentStep === 'confirm' && selectedPackage && (
            <>
              <DialogHeader>
                <DialogTitle>{isRecurringPackage(selectedPackage) ? 'Abo buchen' : 'Paket buchen'}</DialogTitle>
                <DialogDescription>
                  Du bist dabei, {isRecurringPackage(selectedPackage) ? 'das folgende Abo' : 'das folgende Kurspaket'} zu buchen:
                </DialogDescription>
              </DialogHeader>

              <div className="my-2 space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{selectedPackage.name}</span>
                  <div className="text-right">
                    {hasAppliedMemberDiscount(selectedPackage) && (
                      <p className="text-xs text-muted-foreground line-through">
                        {formatPrice(getBasePriceCents(selectedPackage))}
                      </p>
                    )}
                    {hasAppliedMemberDiscount(selectedPackage) && (
                      <>
                        <p className="text-xs font-medium text-primary">
                          {selectedPackage.applied_discount_title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Persönlicher Rabatt auf {formatPrice(getBasePriceCents(selectedPackage))}:{' '}
                          -{formatPrice(selectedPackage.applied_discount_amount_cents ?? 0)}
                        </p>
                      </>
                    )}
                    <span className="text-xl font-bold text-primary">
                      {formatPrice(getEffectivePriceCents(selectedPackage))}
                    </span>
                  </div>
                </div>
                <ul className="space-y-1">
                  {selectedPackage.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckIcon className="h-3 w-3 shrink-0 text-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {selectedIsMemberPlanChange ? (
                  <p className="mt-2 text-xs text-amber-600">
                    Teurere Tarife gelten nach Zahlung sofort, gleich teure Tarife sofort und günstigere Tarife zum nächsten Abrechnungstermin.
                  </p>
                ) : currentSub?.is_active ? (
                  <p className="mt-2 text-xs text-amber-600">
                    Die Credits werden deinem Konto nach erfolgreicher Zahlung gutgeschrieben.
                  </p>
                ) : null}
              </div>

              {selectedIsMemberPlanChange && <SubscriptionPlanChange packageType={selectedPackage.type} onComplete={handleCloseDialog} />}
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handleCloseDialog}>
                  Abbrechen
                </Button>
                {!selectedIsMemberPlanChange && <Button onClick={handleConfirmPurchase}>
                  <CreditCardIcon className="mr-2 h-4 w-4" />
                  Jetzt bezahlen
                </Button>}
              </DialogFooter>
            </>
          )}

          {paymentStep === 'redirecting' && (
            <div className="flex flex-col items-center justify-center gap-4 py-12">
              <Loader2Icon className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg font-semibold">Weiterleitung...</p>
              <p className="text-center text-sm text-muted-foreground">
                Du wirst zum sicheren Zahlungsbereich von Stripe weitergeleitet.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
